import { Search } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { FocusTrap } from "focus-trap-react";
import debounce from "lodash-es/debounce";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import { type ChangeEventHandler, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { SupportedLocaleType } from "../../common/types/languageTypes";
import { SEARCH_TYPE } from "../../common/types/searchTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";
import useSupportedLocale from "../../common/utils/useSupportedLocale";
import searchStore from "../../stores/search/searchStore";
import { getMaxHitsForSearchDomain } from "../../stores/search/searchUtils";
import theme from "../../themes/theme";
import ColumnDropdown from "../common/ColumnDropdown";
import HitsForCmsContent from "./HitsForCmsContent";
import HitsForProduct from "./HitsForProduct";

const hide = () => searchStore.clear(SEARCH_TYPE.QuickSearch, true);

const performQuickSearch = debounce((locale: SupportedLocaleType) => {
   searchStore.performSearches(SEARCH_TYPE.QuickSearch, locale);
}, 85);
const searchDomains = theme.searchConfig.searchDomains;

const SearchField = () => {
   const [location, redirectTo] = useLocation();
   const quickSearchRef = useRef<HTMLDivElement>(null);
   const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
   const locale = useSupportedLocale();
   const isXl = useMediaQuery(mediaQueries.xl);
   const isLg = useMediaQuery(mediaQueries.lg);

   const results = searchStore[SEARCH_TYPE.QuickSearch].results;

   const totalHits: number = results.reduce((total, result) => total + result.hits.length, 0);

   const hasHits: boolean = !!totalHits;

   const isOpen = hasHits && !!searchStore.query;

   const resultsWithHits = results.filter((result) => result.hits.length);

   const hasHitsForMultipleNonProductDomains =
      resultsWithHits.length > 2 ||
      (resultsWithHits.length === 2 && !resultsWithHits.find((result) => result.searchDomainType === "product"));

   const nonProdDomainCN = cn("m-4 md:flex-1");

   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && searchStore.query !== "") {
         redirectTo(`/search?q=${searchStore.query}`);
         hide();
      }
      if (e.key === "Escape") {
         hide();
      }
   };

   const handleChange: ChangeEventHandler<HTMLInputElement> = action((e) => {
      searchStore.query = e.target.value;
      performQuickSearch(locale);
   });
   // Clear the search field if you navigate to a new page, except for the search result page
   useEffect(() => {
      if (location !== "/search") {
         hide();
      }
   }, [location]);

   return (
      <FocusTrap active={isOpen}>
         <div>
            <div ref={setReferenceElement}>
               <Search
                  onChange={handleChange}
                  placeholder="Søk i hele butikken"
                  label=""
                  isLabelHidden={true}
                  value={searchStore.query}
                  autoComplete="off"
                  onKeyDown={handleKeyDown}
               />
            </div>

            {!!referenceElement && (
               <div ref={quickSearchRef}>
                  <ColumnDropdown
                     open={isOpen}
                     onOpenChange={(open) => {
                        if (!open) {
                           hide();
                        }
                     }}
                     referenceElement={referenceElement}
                     withCloseButton
                  >
                     <div className="flex flex-row flex-wrap">
                        {resultsWithHits.map((results) => {
                           const searchDomain = searchDomains.find(
                              (searchDomain) => searchDomain.type === results.searchDomainType
                           );
                           if (searchDomain) {
                              if (results.searchDomainType === "product") {
                                 return (
                                    <div key={searchDomain.name} className="m-4 basis-full">
                                       <HitsForProduct
                                          searchDomain={searchDomain}
                                          maxHits={getMaxHitsForSearchDomain(
                                             results.searchDomainType,
                                             resultsWithHits,
                                             isLg,
                                             isXl
                                          )}
                                       />
                                    </div>
                                 );
                              }
                              return (
                                 <div className={nonProdDomainCN} key={searchDomain.name}>
                                    <HitsForCmsContent
                                       searchDomain={searchDomain}
                                       maxHits={getMaxHitsForSearchDomain(results.searchDomainType, resultsWithHits, isLg, isXl)}
                                       isNarrow={hasHitsForMultipleNonProductDomains}
                                    />
                                 </div>
                              );
                           }
                        })}
                     </div>
                  </ColumnDropdown>
               </div>
            )}
         </div>
      </FocusTrap>
   );
};

export default observer(SearchField);
